<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAbortableRequest } from 'dashboard/composables/useAbortableRequest';
import InboxesAPI from 'dashboard/api/inboxes';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  parseFlowData,
  isValidFlowCta,
  FLOW_BODY_LIMIT,
} from 'dashboard/helper/templateHelper';

const props = defineProps({
  inbox: { type: Object, required: true },
  conversationId: { type: Number, required: true },
  disabled: { type: Boolean, default: false },
});
const { t } = useI18n();
const store = useStore();
const currentChat = useMapGetter('getSelectedChat');
const { run, abort, isPending } = useAbortableRequest();
const show = ref(false);
const flows = ref([]);
const nextCursor = ref(null);
const selectedId = ref('');
const text = ref('');
const cta = ref('');
const action = ref('navigate');
const screen = ref('');
const dataInput = ref('');
const error = ref('');
const sending = ref(false);
const token = ref('');

const supported = computed(
  () =>
    props.inbox.channel_type === 'Channel::Whatsapp' &&
    props.inbox.provider === 'whatsapp_cloud'
);
const canSend = computed(
  () =>
    currentChat.value?.id === props.conversationId &&
    currentChat.value?.can_reply
);
const selectedFlow = computed(() =>
  flows.value.find(flow => flow.id === selectedId.value)
);
const initialData = computed(() => {
  try {
    return { value: parseFlowData(dataInput.value), valid: true };
  } catch {
    return { valid: false };
  }
});
const valid = computed(
  () =>
    selectedFlow.value?.status === 'PUBLISHED' &&
    text.value.trim() &&
    text.value.length <= FLOW_BODY_LIMIT &&
    isValidFlowCta(cta.value) &&
    (action.value === 'data_exchange' ||
      (screen.value.trim() && initialData.value.valid))
);

const loadFlows = async (after = null) => {
  error.value = '';
  try {
    const response = await run(signal =>
      InboxesAPI.getWhatsappFlows(props.inbox.id, after, { signal })
    );
    if (!response) return;
    flows.value = after
      ? [...flows.value, ...response.data.data]
      : response.data.data;
    nextCursor.value = response.data.next_cursor;
  } catch (e) {
    error.value =
      e?.response?.data?.error || t('CONVERSATION.WHATSAPP_FLOW.LOAD_ERROR');
  }
};

const close = () => {
  show.value = false;
  abort();
};
const open = () => {
  flows.value = [];
  selectedId.value = '';
  nextCursor.value = null;
  text.value = '';
  cta.value = '';
  screen.value = '';
  dataInput.value = '';
  action.value = 'navigate';
  token.value = crypto.randomUUID();
  show.value = true;
  loadFlows();
};
const send = async () => {
  if (!valid.value || !canSend.value || sending.value) return;
  sending.value = true;
  error.value = '';
  const flow = {
    flow_id: selectedId.value,
    name: selectedFlow.value.name,
    flow_cta: cta.value,
    flow_action: action.value,
    flow_token: token.value,
  };
  if (action.value === 'navigate') {
    flow.flow_action_payload = { screen: screen.value };
    if (initialData.value.value)
      flow.flow_action_payload.data = initialData.value.value;
  }
  try {
    await store.dispatch('sendWhatsappFlow', {
      conversationId: props.conversationId,
      message: text.value,
      private: false,
      contentAttributes: { whatsapp_flow: flow },
    });
    close();
  } catch (e) {
    error.value = e?.response?.data?.error || t('CONVERSATION.MESSAGE_ERROR');
  } finally {
    sending.value = false;
  }
};

watch(
  () => [props.inbox.id, props.conversationId, props.disabled, supported.value],
  close
);
</script>

<template>
  <Button
    v-if="supported"
    icon="i-ph-list-checks"
    :label="t('CONVERSATION.WHATSAPP_FLOW.SEND')"
    :disabled="disabled || !canSend || sending"
    faded
    slate
    sm
    @click="open"
  />
  <woot-modal v-if="show" v-model:show="show" :on-close="close">
    <woot-modal-header :header-title="t('CONVERSATION.WHATSAPP_FLOW.SEND')" />
    <div class="flex flex-col gap-4 p-6">
      <p v-if="!canSend" class="text-n-ruby-9">
        {{ t('CONVERSATION.WHATSAPP_FLOW.WINDOW_CLOSED') }}
      </p>
      <p v-if="isPending" role="status">
        {{ t('CONVERSATION.WHATSAPP_FLOW.LOADING') }}
      </p>
      <p v-if="error" role="alert" class="text-n-ruby-9">{{ error }}</p>
      <Button
        v-if="error && !flows.length"
        :label="t('CONVERSATION.WHATSAPP_FLOW.RETRY')"
        @click="loadFlows()"
      />
      <label class="flex flex-col gap-2">
        {{ t('CONVERSATION.WHATSAPP_FLOW.SELECT') }}
        <select
          v-model="selectedId"
          class="rounded-lg border border-n-weak bg-n-background p-2"
          :disabled="isPending || sending"
        >
          <option value="">{{ t('CONVERSATION.WHATSAPP_FLOW.SELECT') }}</option>
          <option
            v-for="flow in flows"
            :key="flow.id"
            :value="flow.id"
            :disabled="flow.status !== 'PUBLISHED'"
          >
            {{
              t('CONVERSATION.WHATSAPP_FLOW.OPTION', {
                name: flow.name,
                status: flow.status,
              })
            }}
          </option>
        </select>
      </label>
      <p v-if="!isPending && !error && !flows.length">
        {{ t('CONVERSATION.WHATSAPP_FLOW.EMPTY') }}
      </p>
      <Button
        v-if="nextCursor"
        :label="t('CONVERSATION.WHATSAPP_FLOW.MORE')"
        :disabled="isPending"
        @click="loadFlows(nextCursor)"
      />
      <Input
        v-model="text"
        :label="t('CONVERSATION.WHATSAPP_FLOW.TEXT')"
        :disabled="sending"
      />
      <Input
        v-model="cta"
        :label="t('CONVERSATION.WHATSAPP_FLOW.CTA')"
        :disabled="sending"
      />
      <details open class="flex flex-col gap-3">
        <summary>{{ t('CONVERSATION.WHATSAPP_FLOW.ADVANCED') }}</summary>
        <label class="flex flex-col gap-2 my-3">
          {{ t('CONVERSATION.WHATSAPP_FLOW.ACTION') }}
          <select
            v-model="action"
            class="rounded-lg border border-n-weak bg-n-background p-2"
            :disabled="sending"
          >
            <option value="navigate">
              {{ t('CONVERSATION.WHATSAPP_FLOW.NAVIGATE') }}
            </option>
            <option value="data_exchange">
              {{ t('CONVERSATION.WHATSAPP_FLOW.DATA_EXCHANGE') }}
            </option>
          </select>
        </label>
        <template v-if="action === 'navigate'">
          <Input
            v-model="screen"
            :label="t('CONVERSATION.WHATSAPP_FLOW.SCREEN')"
            :disabled="sending"
          />
          <label class="flex flex-col gap-2 mt-3">
            {{ t('CONVERSATION.WHATSAPP_FLOW.DATA') }}
            <textarea
              v-model="dataInput"
              class="rounded-lg border border-n-weak bg-n-background p-2"
              :disabled="sending"
            />
          </label>
          <p v-if="!initialData.valid" class="text-n-ruby-9">
            {{ t('CONVERSATION.WHATSAPP_FLOW.INVALID_DATA') }}
          </p>
        </template>
      </details>
      <p class="text-sm text-n-slate-11">
        {{ t('CONVERSATION.WHATSAPP_FLOW.TOKEN_HELP') }}
      </p>
      <Button
        :label="t('CONVERSATION.WHATSAPP_FLOW.SEND')"
        :disabled="!valid || !canSend || sending || disabled"
        :is-loading="sending"
        @click="send"
      />
    </div>
  </woot-modal>
</template>
